import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { useDispatch, useSelector } from 'react-redux';
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    EditOutlined,
    LoadingOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import { FixRequestFormFieldValue, IssueEntity, IssueState, ReleaseStatus } from '@/types';
import { RootState } from '@/reducers/reducers';
import { antGrey, getAvatarColorForName, runwayColors, rwColors } from '@/components/styles/globalStyles';
import {
    selectReleasesForAppId,
    selectRequiredFixRequestsApprovals,
    selectUserById,
    selectUserCanPerformAction,
} from '@/selectors/selectors';
import { GetFixRequestCurrentApprovals, getFixRequestFormFieldId, runeCount } from '@/helpers';
import MarkdownComponent from '@/components/HOCs/Markdown';
import MarkdownEditor from '@/components/HOCs/MarkdownEditor';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    TRY_TRANSITION_ISSUE,
    TRY_UPDATE_ISSUE_FIX_REQUEST,
    TriageViewType,
    tryTransitionIssue,
    tryUpdateIssueFixRequest,
} from '@/actions/triage';
import { useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import PermissionedActionButton, { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { FIX_VERSION_CODE_LOCK_TOOLTIP, fixRequestStatusMeta, LABEL_STYLE } from './TriageIssueCard';

interface FixRequestSectionProps {
    orgId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

export const FixRequestSection: React.FC<FixRequestSectionProps> = ({ orgId, issue, viewType }) => {
    const dispatch = useDispatch();
    const fixRequest = issue.fixRequest;
    const requester = useSelector((state: RootState) =>
        fixRequest?.createdBy ? selectUserById(state, orgId, issue.appId, fixRequest.createdBy) : undefined,
    );
    const requesterName = requester ? displayUserNameLong(requester) : (fixRequest?.createdBy ?? '');
    const requiredApprovals = useSelector(selectRequiredFixRequestsApprovals(issue.appId)) || 1;
    const currentApprovals = fixRequest?.status ? GetFixRequestCurrentApprovals(fixRequest.status) : 0;
    const showApprovalCount = requiredApprovals > 1;
    const canApproveReject = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateFixRequestStatus),
    );
    const canManage = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAManageTriageIssues),
    );
    const releases = useSelector((state: RootState) => selectReleasesForAppId(state, issue.appId));
    const isTransitioning = useIsLoading(TRY_TRANSITION_ISSUE, issue.id);
    const isUpdatingFixRequest = useIsLoading(TRY_UPDATE_ISSUE_FIX_REQUEST, issue.id);
    const fixRequestUpdated = useUpdated(TRY_UPDATE_ISSUE_FIX_REQUEST, issue.id);

    const fixVersionOptions = releases
        .filter(
            (release) =>
                ![ReleaseStatus.Completed, ReleaseStatus.Skipped, ReleaseStatus.Deleted].includes(release.status),
        )
        .map((release) => ({ value: release.version, label: release.displayableVersion || release.version }));
    const codeChangesLockVersion = !!fixRequest?.hasCodeChangesPendingOrMerged;
    const canEditFixVersion = canManage && issue.state !== IssueState.FixMerged && !codeChangesLockVersion;
    const fixVersionTooltip =
        codeChangesLockVersion ? FIX_VERSION_CODE_LOCK_TOOLTIP
        : !canManage ? DISALLOWED_ACTION_ERROR_MESSAGE
        : undefined;

    // The fix snapshots its form field settings at creation; edits validate against that snapshot.
    const fixRequestFormFields = fixRequest?.formFieldsSettings ?? [];

    // Per-field inline editing: only one field is editable at a time.
    const [editingFieldId, setEditingFieldId] = React.useState<string | null>(null);
    const [editingFieldValue, setEditingFieldValue] = React.useState<string>('');
    const [editError, setEditError] = React.useState<string | null>(null);
    const [currentFormFieldValues, setCurrentFormFieldValues] = React.useState<FixRequestFormFieldValue[]>(
        fixRequest?.formFieldsValues ?? [],
    );
    const pendingFormFieldValuesRef = React.useRef<FixRequestFormFieldValue[] | null>(null);
    // Tracks whether the caret has been placed at the end of the text for the field just opened, so
    // autofocus lands at the end of any existing value rather than position 0 (and only on first focus).
    const caretPlacedRef = React.useRef(false);

    // The state only captures values at mount — resync on outside changes, but never mid-edit.
    const externalFormFieldValues = fixRequest?.formFieldsValues;
    React.useEffect(() => {
        if (!editingFieldId) {
            setCurrentFormFieldValues(externalFormFieldValues ?? []);
        }
    }, [externalFormFieldValues, editingFieldId]);

    // Live validation — runs whenever the editing field or its value changes.
    React.useEffect(() => {
        if (!editingFieldId) return;
        const field = fixRequestFormFields.find((f) => getFixRequestFormFieldId(f) === editingFieldId);
        if (!field) return;
        const trimmed = editingFieldValue.trim();
        const trimmedRuneCount = runeCount(trimmed);
        if (field.required && trimmed.length === 0) {
            setEditError(`${field.name} is required`);
        } else if (
            field.minCharacters &&
            field.minCharacters > 0 &&
            trimmedRuneCount > 0 &&
            trimmedRuneCount < field.minCharacters
        ) {
            setEditError(`${field.name} is too short (${trimmedRuneCount}/${field.minCharacters})`);
        } else {
            setEditError(null);
        }
    }, [editingFieldId, editingFieldValue]);

    const startEditingField = (fieldId: string, currentValue: string) => {
        setEditingFieldValue(currentValue);
        setEditError(null);
        caretPlacedRef.current = false;
        setEditingFieldId(fieldId);
    };

    const saveFieldEdit = () => {
        const fieldDef = fixRequestFormFields.find((f) => getFixRequestFormFieldId(f) === editingFieldId);
        if (fieldDef?.required && !editingFieldValue.trim()) {
            setEditError(`${fieldDef.name} is required`);
            return;
        }
        const trimmedRuneCount = runeCount(editingFieldValue.trim());
        if (fieldDef?.minCharacters && fieldDef.minCharacters > 0 && trimmedRuneCount < fieldDef.minCharacters) {
            setEditError(`${fieldDef.name} is too short (${trimmedRuneCount}/${fieldDef.minCharacters})`);
            return;
        }
        setEditError(null);
        const formFieldsValues: FixRequestFormFieldValue[] = fixRequestFormFields.map((field) => {
            const fieldId = getFixRequestFormFieldId(field);
            if (fieldId === editingFieldId) {
                return { fieldId, value: editingFieldValue };
            }
            const existing = currentFormFieldValues.find((v) => v.fieldId === fieldId);
            return { fieldId, value: existing?.value ?? '' };
        });
        pendingFormFieldValuesRef.current = formFieldsValues;
        dispatch(tryUpdateIssueFixRequest({ orgId, appId: issue.appId, issueId: issue.id, formFieldsValues }));
        // Field stays open — closed by the success effect below.
    };

    // On a successful update, commit the optimistic values and close the active field editor.
    React.useEffect(() => {
        if (fixRequestUpdated && editingFieldId) {
            if (pendingFormFieldValuesRef.current !== null) {
                setCurrentFormFieldValues(pendingFormFieldValuesRef.current);
                pendingFormFieldValuesRef.current = null;
            }
            setEditingFieldId(null);
        }
    }, [fixRequestUpdated]);

    const transition = (targetState: IssueState) =>
        dispatch(tryTransitionIssue({ orgId, appId: issue.appId, issueId: issue.id, targetState, viewType }));

    if (!fixRequest) {
        return null;
    }

    return (
        <>
            <Collapse
                className="triage-collapse"
                ghost
                bordered={false}
                defaultActiveKey={['section']}
                items={[
                    {
                        key: 'section',
                        showArrow: false,
                        style: {
                            borderTop: issue.potentialCauses?.causes?.length ? `1px solid ${antGrey.grey4}` : undefined,
                        },
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <ToolOutlined style={{ color: antGrey.grey7 }} />
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Fix request</Text>
                            </div>
                        ),
                        extra: (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {fixRequestStatusMeta(issue.state).icon}
                                <Text style={LABEL_STYLE}>{fixRequestStatusMeta(issue.state).label}</Text>
                            </div>
                        ),
                        children: (
                            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {fixRequestFormFields.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {fixRequestFormFields.map((field) => {
                                            const fieldId = getFixRequestFormFieldId(field);
                                            const displayVal = currentFormFieldValues.find(
                                                (v) => v.fieldId === fieldId,
                                            )?.value;
                                            const isEditing = editingFieldId === fieldId;

                                            return (
                                                <div
                                                    key={fieldId}
                                                    style={{
                                                        border: `1px solid ${antGrey.grey4}`,
                                                        borderRadius: 8,
                                                        padding: '8px 10px',
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: 6,
                                                        }}
                                                    >
                                                        <Text
                                                            style={{
                                                                textTransform: 'uppercase',
                                                                fontSize: 11,
                                                                fontWeight: 500,
                                                                letterSpacing: '0.08em',
                                                                color: antGrey.grey7,
                                                            }}
                                                        >
                                                            {field.name}
                                                            {isEditing && field.required && (
                                                                <Text
                                                                    type="danger"
                                                                    style={{ marginLeft: 3, fontSize: 11 }}
                                                                >
                                                                    *
                                                                </Text>
                                                            )}
                                                        </Text>
                                                        {!isEditing && (
                                                            <PermissionedActionButton
                                                                type="link"
                                                                size="small"
                                                                actionType={RunwayUserAction.UAManageTriageIssues}
                                                                style={{ padding: 0, height: 'auto', lineHeight: 1 }}
                                                                icon={<EditOutlined />}
                                                                onClick={(e) => {
                                                                    startEditingField(fieldId, displayVal ?? '');
                                                                    e.stopPropagation();
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    {isEditing ?
                                                        <>
                                                            <div style={{ marginBottom: 4 }}>
                                                                <MarkdownEditor
                                                                    autoFocus
                                                                    preview="edit"
                                                                    value={editingFieldValue}
                                                                    onChange={(val) => setEditingFieldValue(val ?? '')}
                                                                    textareaProps={{
                                                                        disabled: isUpdatingFixRequest,
                                                                        placeholder: field.placeholder || undefined,
                                                                        // On the initial autofocus, move the caret to
                                                                        // the end of any existing text instead of 0.
                                                                        onFocus: (e) => {
                                                                            if (caretPlacedRef.current) return;
                                                                            caretPlacedRef.current = true;
                                                                            const len = e.target.value.length;
                                                                            e.target.setSelectionRange(len, len);
                                                                        },
                                                                    }}
                                                                />
                                                            </div>
                                                            {editError && (
                                                                <Text
                                                                    type="danger"
                                                                    style={{ display: 'block', marginBottom: 12 }}
                                                                >
                                                                    {editError}
                                                                </Text>
                                                            )}
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'flex-end',
                                                                    gap: 4,
                                                                    marginTop: 8,
                                                                }}
                                                            >
                                                                <Button
                                                                    type="link"
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        setEditingFieldId(null);
                                                                        setEditError(null);
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <PermissionedActionButton
                                                                    type="primary"
                                                                    size="small"
                                                                    actionType={RunwayUserAction.UAManageTriageIssues}
                                                                    loading={isUpdatingFixRequest}
                                                                    onClick={(e) => {
                                                                        saveFieldEdit();
                                                                        e.stopPropagation();
                                                                    }}
                                                                >
                                                                    Save
                                                                </PermissionedActionButton>
                                                            </div>
                                                        </>
                                                    : displayVal ?
                                                        <MarkdownComponent
                                                            source={displayVal}
                                                            style={{ fontSize: 15 }}
                                                        />
                                                    :   <Text type="secondary" style={{ display: 'block' }}>
                                                            -
                                                        </Text>
                                                    }
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 6,
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Target version</Text>
                                        <Tooltip title={fixVersionTooltip}>
                                            <Select
                                                value={fixRequest.fixVersion}
                                                options={fixVersionOptions}
                                                showSearch
                                                optionFilterProp="label"
                                                disabled={isUpdatingFixRequest || !canEditFixVersion}
                                                suffixIcon={
                                                    isUpdatingFixRequest ?
                                                        <LoadingOutlined spin style={{ color: antGrey.grey6 }} />
                                                    :   undefined
                                                }
                                                placeholder="--"
                                                style={{ width: '100%' }}
                                                onChange={(value) =>
                                                    dispatch(
                                                        tryUpdateIssueFixRequest({
                                                            orgId,
                                                            appId: issue.appId,
                                                            issueId: issue.id,
                                                            version: value,
                                                        }),
                                                    )
                                                }
                                            />
                                        </Tooltip>
                                    </div>
                                    {requesterName && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 6,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Requested by</Text>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    minHeight: 32,
                                                    padding: '3px 4px',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    border: `1px solid ${antGrey.grey5}`,
                                                    borderRadius: 6,
                                                    background: antGrey.grey2,
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        padding: '1px 8px',
                                                        borderRadius: 4,
                                                        background: antGrey.grey3,
                                                    }}
                                                >
                                                    <Avatar
                                                        size={20}
                                                        src={requester?.commsAvatars?.image32}
                                                        style={{
                                                            backgroundColor: getAvatarColorForName(requesterName),
                                                            fontSize: 11,
                                                            border: 'none',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {requesterName.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                                        {requesterName}
                                                    </Text>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {issue.state === IssueState.FixRequested && (
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                type="primary"
                                                icon={<CheckCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canApproveReject}
                                                onClick={() => transition(IssueState.FixApproved)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor:
                                                        canApproveReject ? rwColors.statusGreen : undefined,
                                                    borderColor: canApproveReject ? rwColors.statusGreen : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Approve fix request
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title={canApproveReject ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                            <Button
                                                icon={<CloseCircleOutlined />}
                                                loading={isTransitioning}
                                                disabled={!canApproveReject}
                                                onClick={() => transition(IssueState.FixRejected)}
                                                style={{
                                                    flex: 1,
                                                    height: 40,
                                                    backgroundColor: canApproveReject ? rwColors.statusRed : undefined,
                                                    borderColor: canApproveReject ? rwColors.statusRed : undefined,
                                                    color: canApproveReject ? runwayColors.white : undefined,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Reject fix request
                                            </Button>
                                        </Tooltip>
                                        {showApprovalCount && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    color: rwColors.statusYellow,
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <ClockCircleOutlined />
                                                <Text style={{ color: antGrey.grey8, fontWeight: 600, fontSize: 20 }}>
                                                    {currentApprovals} of {requiredApprovals}
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
};
